import { useState, useEffect } from 'react';
import { Link2, ClipboardList, Home, FileText, AppWindow, Ruler, Plus, Copy, AlertTriangle, Search, X, ChevronsDownUp, ChevronsUpDown, ArrowDownToLine, ArrowUpToLine } from 'lucide-react';
import WindowRow from './WindowRow';
import { COLORS, WINDOW_TYPES } from '../../config/windowTypes';
import { getAllJobs } from '../../services/cutlist-jobs';
import { useJobContext } from '../../contexts/CutlistJobContext';

const WALNUT = '#8B4513';

// Shared "Jump to" nav strip — rendered above and below the window list so
// you can get back to a specific window (or the opposite end of the list)
// without scrolling through everything in between.
function JumpStrip({ windows, onJump, edgeLabel, edgeIcon: EdgeIcon, onEdgeClick }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: '8px',
      marginBottom: '16px', padding: '10px 14px',
      background: 'white', borderRadius: '10px',
      border: '1px solid rgba(139,69,19,0.1)',
      overflowX: 'auto',
    }}>
      <span style={{ fontSize: '11px', fontWeight: '700', color: '#999', textTransform: 'uppercase', letterSpacing: '0.05em', flexShrink: 0 }}>
        Jump to
      </span>
      {windows.map((w, i) => (
        <button
          key={w.id}
          onClick={() => onJump(w.id)}
          title={w.label ? `Window ${i + 1} — ${w.label}` : `Window ${i + 1}`}
          style={{
            flexShrink: 0,
            padding: '5px 12px', borderRadius: '999px',
            border: '1px solid rgba(139,69,19,0.2)',
            background: 'rgba(139,69,19,0.06)', color: WALNUT,
            fontSize: '12px', fontWeight: '600', cursor: 'pointer', fontFamily: 'inherit',
            whiteSpace: 'nowrap',
          }}
        >
          {i + 1}{w.label ? ` — ${w.label}` : ''}
        </button>
      ))}
      <div style={{ flex: 1, minWidth: '4px' }} />
      <button
        onClick={onEdgeClick}
        style={{
          flexShrink: 0,
          display: 'flex', alignItems: 'center', gap: '5px',
          padding: '5px 12px', borderRadius: '999px',
          border: '1px solid rgba(139,69,19,0.2)',
          background: 'transparent', color: WALNUT,
          fontSize: '12px', fontWeight: '600', cursor: 'pointer', fontFamily: 'inherit',
          whiteSpace: 'nowrap',
        }}
      >
        <EdgeIcon size={12} strokeWidth={2} /> {edgeLabel}
      </button>
    </div>
  );
}

// Fields copied by a window's "Copy to All" action — the settings a job
// typically shares across every window, so re-picking them per window is
// repetitive busywork. Deliberately excludes size/type/label (per-window by
// definition) and casement-only fields (would silently no-op on non-CSMT rows).
const COPYABLE_FIELDS = ['finType', 'color', 'screenType', 'screenSize', 'meshType', 'gasFill'];

export default function CutListForm({ job, onUpdateJob, onAddWindow, onUpdateWindow, onRemoveWindow, onDuplicateWindow, onCalculate, errors }) {
  const { loadFromJob, linkedJobId } = useJobContext();
  const [firebaseJobs, setFirebaseJobs] = useState([]);
  const [loadingJobs, setLoadingJobs]   = useState(false);
  const [pickerOpen, setPickerOpen]     = useState(false);
  const [searchQuery, setSearchQuery]   = useState('');
  const [collapsedIds, setCollapsedIds] = useState(() => new Set());

  function toggleCollapse(id) {
    setCollapsedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  const allCollapsed = job.windows.length > 0 && job.windows.every(w => collapsedIds.has(w.id));
  function toggleCollapseAll() {
    setCollapsedIds(allCollapsed ? new Set() : new Set(job.windows.map(w => w.id)));
  }

  // Jump to a window — un-filters, expands it if collapsed, then scrolls it
  // into view. The double rAF gives React one render/paint cycle to apply
  // those state changes before we measure/scroll to the target element.
  function jumpToWindow(id) {
    setSearchQuery('');
    setCollapsedIds(prev => {
      if (!prev.has(id)) return prev;
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        document.getElementById(`cutlist-window-${id}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    });
  }

  function jumpToBottom() {
    document.getElementById('cutlist-form-bottom')?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }

  function jumpToTop() {
    document.getElementById('cutlist-form-top')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  function handleCopyToAll(sourceId) {
    const source = job.windows.find(w => w.id === sourceId);
    if (!source) return;
    job.windows.forEach(w => {
      if (w.id === sourceId) return;
      COPYABLE_FIELDS.forEach(f => {
        if (source[f] !== undefined) onUpdateWindow(w.id, f, source[f]);
      });
    });
  }

  const q = searchQuery.trim().toLowerCase();
  const numberedWindows = job.windows.map((w, i) => ({ w, i }));
  const visibleWindows = q
    ? numberedWindows.filter(({ w }) => {
        const typeLabel = WINDOW_TYPES?.find(t => t.code === w.type)?.label ?? '';
        const hay = `${w.label || ''} ${w.type || ''} ${typeLabel} ${w.color || ''}`.toLowerCase();
        return hay.includes(q);
      })
    : numberedWindows;

  useEffect(() => {
    loadFirebaseJobs();
  }, []);

  async function loadFirebaseJobs() {
    setLoadingJobs(true);
    try {
      const jobs = await getAllJobs();
      setFirebaseJobs(jobs);
    } catch (e) {
      console.error('Failed to load jobs:', e);
    } finally {
      setLoadingJobs(false);
    }
  }

  function handlePickJob(firebaseJob) {
    loadFromJob(firebaseJob);
    setPickerOpen(false);
  }

  const linkedJob = firebaseJobs.find(j => j.id === linkedJobId);

  return (
    <div style={{ background: 'transparent', minHeight: '100vh', padding: '8px 0' }}>
      <div style={{ maxWidth: '1100px', margin: '0 auto' }}>

        {/* ── Job Picker Banner ─────────────────────────────────────────── */}
        <div style={{
          background: 'white',
          borderRadius: '16px',
          boxShadow: '0 4px 24px rgba(139,69,19,0.08)',
          border: '1px solid rgba(139,69,19,0.1)',
          padding: '16px 24px',
          marginBottom: '16px',
          display: 'flex',
          alignItems: 'center',
          gap: '16px',
          flexWrap: 'wrap',
        }}>
          <Link2 size={20} strokeWidth={2} color={WALNUT} />
          <div style={{ flex: 1 }}>
            {linkedJob ? (
              <div>
                <div style={{ fontSize: '12px', fontWeight: '700', color: '#8B4513', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  Linked to Job
                </div>
                <div style={{ fontSize: '14px', fontWeight: '600', color: '#1a1a1a', marginTop: '2px' }}>
                  {linkedJob.customerName}
                  {linkedJob.woNumber && <span style={{ fontSize: '12px', color: '#aaa', marginLeft: '8px' }}>WO# {linkedJob.woNumber}</span>}
                  {linkedJob.jobName  && <span style={{ fontSize: '12px', color: '#8B4513', fontStyle: 'italic', marginLeft: '8px' }}>{linkedJob.jobName}</span>}
                </div>
              </div>
            ) : (
              <div>
                <div style={{ fontSize: '13px', fontWeight: '600', color: '#555' }}>
                  Link to an existing job to auto-fill customer info
                </div>
                <div style={{ fontSize: '11px', color: '#aaa', marginTop: '2px' }}>
                  Or fill in the fields below manually
                </div>
              </div>
            )}
          </div>

          <button
            onClick={() => { setPickerOpen(!pickerOpen); loadFirebaseJobs(); }}
            style={{
              padding: '8px 18px',
              borderRadius: '10px',
              border: '1px solid rgba(139,69,19,0.2)',
              background: linkedJob ? 'rgba(139,69,19,0.06)' : WALNUT,
              color: linkedJob ? '#8B4513' : 'white',
              fontSize: '12px',
              fontWeight: '700',
              cursor: 'pointer',
              transition: 'all 0.15s',
              whiteSpace: 'nowrap',
              display: 'flex', alignItems: 'center', gap: '6px',
            }}
          >
            {linkedJob ? <>↺ Change Job</> : <><ClipboardList size={14} strokeWidth={2} /> Pick a Job</>}
          </button>
        </div>

        {/* ── Job Picker Dropdown ───────────────────────────────────────── */}
        {pickerOpen && (
          <div style={{
            background: 'white',
            borderRadius: '14px',
            border: '1px solid rgba(139,69,19,0.15)',
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
            marginBottom: '16px',
            overflow: 'hidden',
          }}>
            <div style={{
              padding: '12px 20px',
              background: WALNUT,
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            }}>
              <span style={{ color: 'white', fontWeight: '700', fontSize: '13px' }}>
                Select a Job to Link
              </span>
              <button
                onClick={() => setPickerOpen(false)}
                style={{ background: 'none', border: 'none', color: 'white', fontSize: '18px', cursor: 'pointer', padding: '0' }}
              >
                ×
              </button>
            </div>

            {loadingJobs ? (
              <div style={{ padding: '24px', textAlign: 'center', color: '#aaa', fontSize: '13px' }}>
                Loading jobs...
              </div>
            ) : firebaseJobs.length === 0 ? (
              <div style={{ padding: '24px', textAlign: 'center', color: '#aaa', fontSize: '13px' }}>
                No jobs found — create one in the Dashboard first
              </div>
            ) : (
              <div style={{ maxHeight: '280px', overflowY: 'auto' }}>
                {firebaseJobs.map((j, idx) => {
                  const date = j.createdAt?.toDate
                    ? j.createdAt.toDate().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                    : '';
                  return (
                    <div
                      key={j.id}
                      onClick={() => handlePickJob(j)}
                      style={{
                        padding: '12px 20px',
                        borderBottom: idx < firebaseJobs.length - 1 ? '1px solid rgba(139,69,19,0.06)' : 'none',
                        background: j.id === linkedJobId ? 'rgba(139,69,19,0.06)' : idx % 2 === 0 ? 'white' : '#fdfcfb',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '14px',
                        transition: 'background 0.15s',
                      }}
                      onMouseEnter={e => e.currentTarget.style.background = 'rgba(139,69,19,0.08)'}
                      onMouseLeave={e => e.currentTarget.style.background = j.id === linkedJobId ? 'rgba(139,69,19,0.06)' : idx % 2 === 0 ? 'white' : '#fdfcfb'}
                    >
                      <Home size={20} strokeWidth={2} color={WALNUT} />
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: '13px', fontWeight: '700', color: '#1a1a1a' }}>
                          {j.customerName || 'Unnamed'}
                          {j.id === linkedJobId && (
                            <span style={{ marginLeft: '8px', fontSize: '10px', background: '#8B4513', color: 'white', padding: '1px 6px', borderRadius: '4px' }}>
                              Current
                            </span>
                          )}
                        </div>
                        <div style={{ fontSize: '11px', color: '#aaa', marginTop: '2px', display: 'flex', gap: '12px' }}>
                          {j.woNumber && <span>WO# {j.woNumber}</span>}
                          {j.jobName  && <span style={{ color: '#8B4513', fontStyle: 'italic' }}>{j.jobName}</span>}
                          {date       && <span>{date}</span>}
                        </div>
                      </div>
                      <span style={{ fontSize: '11px', color: '#8B4513', fontWeight: '600' }}>
                        Select →
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* ── Job Information Card ──────────────────────────────────────── */}
        <div style={{
          background: 'white',
          borderRadius: '16px',
          boxShadow: '0 4px 24px rgba(139,69,19,0.08), 0 1px 4px rgba(0,0,0,0.04)',
          border: linkedJob ? '1.5px solid rgba(139,69,19,0.25)' : '1px solid rgba(139,69,19,0.1)',
          padding: '28px',
          marginBottom: '24px',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
            <div style={{
              width: '36px', height: '36px', borderRadius: '10px',
              background: WALNUT,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              boxShadow: '0 4px 8px rgba(139,69,19,0.25)',
            }}>
              <FileText size={16} strokeWidth={2} color="white" />
            </div>
            <div>
              <h2 style={{ margin: 0, fontSize: '16px', fontWeight: '700', color: '#2b2b2b' }}>Job Information</h2>
              <p style={{ margin: 0, fontSize: '11px', color: '#999' }}>
                {linkedJob ? 'Auto-filled from linked job — edit if needed' : 'Enter customer and order details'}
              </p>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px' }}>
            {[
              { label: 'Customer Name', key: 'customerName', type: 'text', placeholder: 'e.g. David Cahow'      },
              { label: 'Job Name',      key: 'jobName',      type: 'text', placeholder: 'e.g. Kitchen Remodel'  },
              { label: 'WO Number',     key: 'woNumber',     type: 'text', placeholder: 'e.g. 8834'             },
              { label: 'Order Date',    key: 'orderDate',    type: 'date', placeholder: ''                       },
            ].map(field => (
              <div key={field.key}>
                <label style={{ display: 'block', fontSize: '11px', fontWeight: '600', color: '#8B4513', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  {field.label}
                </label>
                <input
                  type={field.type}
                  value={job[field.key]}
                  onChange={e => onUpdateJob(field.key, e.target.value)}
                  placeholder={field.placeholder}
                  style={{
                    width: '100%', boxSizing: 'border-box',
                    border: '1.5px solid #e8e0d8',
                    borderRadius: '10px',
                    padding: '10px 14px',
                    fontSize: '13px',
                    color: '#2b2b2b',
                    background: linkedJob ? 'rgba(139,69,19,0.03)' : '#fdfcfb',
                    outline: 'none',
                    transition: 'border-color 0.2s, box-shadow 0.2s',
                  }}
                  onFocus={e => { e.target.style.borderColor = '#8B4513'; e.target.style.boxShadow = '0 0 0 3px rgba(139,69,19,0.08)'; }}
                  onBlur={e => { e.target.style.borderColor = '#e8e0d8'; e.target.style.boxShadow = 'none'; }}
                />
              </div>
            ))}

            <div>
              <label style={{ display: 'block', fontSize: '11px', fontWeight: '600', color: '#8B4513', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Default Color
              </label>
              <select
                value={job.color}
                onChange={e => onUpdateJob('color', e.target.value)}
                style={{
                  width: '100%', boxSizing: 'border-box',
                  border: '1.5px solid #e8e0d8',
                  borderRadius: '10px',
                  padding: '10px 14px',
                  fontSize: '13px',
                  color: '#2b2b2b',
                  background: '#fdfcfb',
                  outline: 'none',
                  cursor: 'pointer',
                  transition: 'border-color 0.2s, box-shadow 0.2s',
                }}
                onFocus={e => { e.target.style.borderColor = '#8B4513'; e.target.style.boxShadow = '0 0 0 3px rgba(139,69,19,0.08)'; }}
                onBlur={e => { e.target.style.borderColor = '#e8e0d8'; e.target.style.boxShadow = 'none'; }}
              >
                {COLORS.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
          </div>
        </div>

        {/* ── Windows Header ────────────────────────────────────────────── */}
        <div id="cutlist-form-top" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px', padding: '0 4px', flexWrap: 'wrap', gap: '10px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <h2 style={{ margin: 0, fontSize: '16px', fontWeight: '700', color: '#2b2b2b' }}>Windows</h2>
            {job.windows.length > 0 && (
              <span style={{
                background: WALNUT,
                color: 'white', fontSize: '11px', fontWeight: '600',
                padding: '2px 10px', borderRadius: '999px',
                boxShadow: '0 2px 6px rgba(139,69,19,0.3)',
              }}>
                {job.windows.length}
              </span>
            )}
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
            {job.windows.length > 3 && (
              <div style={{
                display: 'flex', alignItems: 'center', gap: '6px',
                background: 'white', borderRadius: '10px', padding: '8px 12px',
                border: '1px solid rgba(139,69,19,0.15)',
              }}>
                <Search size={14} strokeWidth={2} color="#999" style={{ flexShrink: 0 }} />
                <input
                  type="text"
                  name="cutlist-window-search"
                  autoComplete="off"
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  placeholder="Search windows…"
                  style={{ border: 'none', outline: 'none', fontSize: '12.5px', color: '#2b2b2b', fontFamily: 'inherit', background: 'transparent', width: '150px' }}
                />
                {searchQuery && (
                  <button onClick={() => setSearchQuery('')} style={{ display: 'flex', border: 'none', background: 'transparent', color: '#999', cursor: 'pointer', padding: 0 }}>
                    <X size={13} strokeWidth={2} />
                  </button>
                )}
              </div>
            )}

            {job.windows.length > 1 && (
              <button
                onClick={toggleCollapseAll}
                style={{
                  display: 'flex', alignItems: 'center', gap: '6px',
                  padding: '8px 14px', borderRadius: '10px',
                  border: '1px solid rgba(139,69,19,0.2)',
                  background: 'rgba(139,69,19,0.06)', color: WALNUT,
                  fontSize: '12.5px', fontWeight: '600', cursor: 'pointer', fontFamily: 'inherit',
                  whiteSpace: 'nowrap',
                }}
              >
                {allCollapsed ? <ChevronsUpDown size={14} strokeWidth={2} /> : <ChevronsDownUp size={14} strokeWidth={2} />}
                {allCollapsed ? 'Expand All' : 'Collapse All'}
              </button>
            )}

            <button
              onClick={onAddWindow}
              style={{
                padding: '10px 20px',
                background: WALNUT,
                color: 'white', border: 'none', borderRadius: '10px',
                fontSize: '13px', fontWeight: '600', cursor: 'pointer',
                boxShadow: '0 4px 12px rgba(139,69,19,0.3)',
                display: 'flex', alignItems: 'center', gap: '6px',
                transition: 'all 0.2s ease', whiteSpace: 'nowrap',
              }}
              onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-1px)'; e.currentTarget.style.boxShadow = '0 6px 16px rgba(139,69,19,0.4)'; }}
              onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 4px 12px rgba(139,69,19,0.3)'; }}
            >
              <Plus size={16} strokeWidth={2.5} /> Add Window
            </button>
          </div>
        </div>

        {/* Jump-to strip — quick nav once a job has more than one window */}
        {job.windows.length > 1 && (
          <JumpStrip windows={job.windows} onJump={jumpToWindow} edgeLabel="Bottom" edgeIcon={ArrowDownToLine} onEdgeClick={jumpToBottom} />
        )}

        {/* Empty state — no windows at all */}
        {job.windows.length === 0 && (
          <div style={{
            background: 'white', borderRadius: '16px',
            border: '2px dashed rgba(139,69,19,0.15)',
            padding: '64px 32px', textAlign: 'center',
            boxShadow: '0 2px 12px rgba(0,0,0,0.03)',
          }}>
            <div style={{ marginBottom: '16px', opacity: 0.4, display: 'flex', justifyContent: 'center' }}>
              <AppWindow size={48} strokeWidth={1.5} color={WALNUT} />
            </div>
            <p style={{ margin: '0 0 6px 0', fontSize: '16px', fontWeight: '600', color: '#aaa' }}>No windows added yet</p>
            <p style={{ margin: 0, fontSize: '13px', color: '#ccc' }}>Click "Add Window" to get started</p>
          </div>
        )}

        {/* Empty state — search matched nothing */}
        {job.windows.length > 0 && visibleWindows.length === 0 && (
          <div style={{
            background: 'white', borderRadius: '16px',
            border: '1px solid rgba(139,69,19,0.1)',
            padding: '32px', textAlign: 'center',
            boxShadow: '0 2px 12px rgba(0,0,0,0.03)',
          }}>
            <p style={{ margin: '0 0 10px 0', fontSize: '13px', color: '#999', fontWeight: '600' }}>No windows match your search.</p>
            <button
              onClick={() => setSearchQuery('')}
              style={{ padding: '7px 16px', borderRadius: '8px', border: `1px solid ${WALNUT}`, background: 'transparent', color: WALNUT, fontSize: '12.5px', fontWeight: '600', cursor: 'pointer', fontFamily: 'inherit' }}
            >
              Clear
            </button>
          </div>
        )}

        {/* Window rows */}
        {visibleWindows.map(({ w, i }) => (
          <div key={w.id} id={`cutlist-window-${w.id}`}>
            <WindowRow
              window={w}
              index={i}
              onUpdate={onUpdateWindow}
              onRemove={onRemoveWindow}
              onDuplicate={onDuplicateWindow}
              onCopyToAll={job.windows.length > 1 ? handleCopyToAll : null}
              collapsed={collapsedIds.has(w.id)}
              onToggleCollapse={() => toggleCollapse(w.id)}
            />
          </div>
        ))}

        {/* Errors */}
        {errors.length > 0 && (
          <div style={{
            background: '#fff5f5', border: '1px solid #ffcdd2',
            borderRadius: '12px', padding: '16px', marginTop: '16px',
            boxShadow: '0 2px 8px rgba(220,0,0,0.05)',
          }}>
            {errors.map((e, i) => (
              <p key={i} style={{ margin: i === 0 ? 0 : '6px 0 0 0', fontSize: '13px', color: '#c62828', display: 'flex', alignItems: 'center', gap: '6px' }}>
                <AlertTriangle size={14} strokeWidth={2} /> {e}
              </p>
            ))}
          </div>
        )}

        {/* Jump-to strip — mirrored at the bottom of the list, so you don't
            have to scroll all the way back up to get to a specific window */}
        {job.windows.length > 1 && (
          <div style={{ marginTop: '16px' }}>
            <JumpStrip windows={job.windows} onJump={jumpToWindow} edgeLabel="Top" edgeIcon={ArrowUpToLine} onEdgeClick={jumpToTop} />
          </div>
        )}

        {/* Bottom Add/Duplicate buttons — visible when windows exist */}
        {job.windows.length > 0 && (
          <div style={{ display: 'flex', gap: '10px', marginTop: '16px' }}>
            <button
              onClick={onAddWindow}
              style={{
                flex: 1, padding: '12px 16px',
                background: WALNUT,
                color: 'white', border: 'none', borderRadius: '10px',
                fontSize: '14px', fontWeight: '600', cursor: 'pointer',
                boxShadow: '0 4px 12px rgba(139,69,19,0.3)',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
              }}
            >
              <Plus size={16} strokeWidth={2.5} /> Add Window
            </button>
            <button
              onClick={() => onDuplicateWindow(job.windows[job.windows.length - 1].id)}
              style={{
                flex: 1, padding: '12px 16px',
                background: 'white',
                color: '#8B4513', border: '2px solid rgba(139,69,19,0.3)', borderRadius: '10px',
                fontSize: '14px', fontWeight: '600', cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
              }}
            >
              <Copy size={16} strokeWidth={2} /> Duplicate Last
            </button>
          </div>
        )}

        {/* Calculate button */}
        {job.windows.length > 0 && (
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '24px', paddingBottom: '32px' }}>
            <button
              onClick={onCalculate}
              style={{
                padding: '14px 40px',
                background: WALNUT,
                color: 'white', border: 'none', borderRadius: '12px',
                fontSize: '15px', fontWeight: '700', cursor: 'pointer',
                boxShadow: '0 6px 20px rgba(139,69,19,0.35)',
                display: 'flex', alignItems: 'center', gap: '10px',
                transition: 'all 0.2s ease',
                letterSpacing: '0.02em',
              }}
              onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 10px 28px rgba(139,69,19,0.45)'; }}
              onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 6px 20px rgba(139,69,19,0.35)'; }}
            >
              <Ruler size={18} strokeWidth={2} />
              Calculate Cut List
            </button>
          </div>
        )}

        <div id="cutlist-form-bottom" />
      </div>
    </div>
  );
}